import {
  PATIENT_ANNOUNCEMENT_BANNER_DEV_CONSOLE_VARIANT_NAME,
  type PatientAnnouncementBanner,
  type PatientAnnouncementBannerRule,
} from '@dev-console/patient-announcement-banner';
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';

import { BannerPreview } from '@/components/patient-banner/banner-preview';

interface ConfirmBannerDialogProps {
  rule: PatientAnnouncementBannerRule;
  // null turns the banner off.
  banner: PatientAnnouncementBanner | null;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ConfirmBannerDialog({ rule, banner, isSaving, onCancel, onConfirm }: ConfirmBannerDialogProps) {
  const titleId = useId();
  const isClear = banner == null;
  const environments = rule.environments?.join(', ') ?? 'all environments';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSaving) onCancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isSaving, onCancel]);

  return createPortal(
    <div
      role='dialog'
      aria-modal='true'
      aria-labelledby={titleId}
      className='fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4'
    >
      <div className='flex max-h-[calc(100vh-2rem)] w-full max-w-lg flex-col rounded-lg bg-white shadow-xl'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <h3 id={titleId} className='text-lg font-medium text-neutral-900'>
            {isClear ? 'Turn off patient banner' : 'Show patient banner'}
          </h3>
        </div>

        <div className='min-h-0 space-y-4 overflow-y-auto px-6 py-5 text-sm text-neutral-700'>
          <p>
            {isClear ? 'This removes the banner for' : 'This shows the banner below to'} every patient matching the{' '}
            <span className='font-medium text-neutral-900'>{rule.name}</span> rule ({environments}). Patients see the
            change the next time the app refreshes its Statsig values.
          </p>
          {banner != null && <BannerPreview value={banner} />}
          {rule.variants.length > 0 && (
            <p className='text-neutral-500'>
              This rule splits traffic across variants. Every other variant is set to 0% and the{' '}
              <span className='font-mono text-xs'>{PATIENT_ANNOUNCEMENT_BANNER_DEV_CONSOLE_VARIANT_NAME}</span> variant
              is set to 100%.
            </p>
          )}
        </div>

        <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
          <button
            type='button'
            disabled={isSaving}
            onClick={onCancel}
            className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50'
          >
            Cancel
          </button>
          <button
            type='button'
            disabled={isSaving}
            onClick={onConfirm}
            className={`rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
              isClear ? 'bg-red-600 hover:bg-red-700' : 'bg-neutral-900 hover:bg-neutral-800'
            }`}
          >
            {isSaving ? 'Saving…' : isClear ? 'Turn off banner' : 'Show banner'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
